//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { AgentServiceError } from "serviceHooks/useAgentService";
import { AgentTrackingContext, trackAgent } from "utils/helpers/agentTracking";
//#endregion
//#region components
import useAgentService from "serviceHooks/useAgentService";
import { citedFaqArticles } from "./agentFaqLinks";
//#endregion

/** LLM.Service's limit on one message: a longer case is cut rather than refused. */
const MaxQuestionChars = 4000;

// Whatever goes wrong in the background stays there: her case goes on as if it never ran.
const quietly = (work: () => Promise<void> | void) => {
  try {
    void Promise.resolve(work()).catch(() => undefined);
  } catch {
    // Nothing to show her and nothing to retry.
  }
};

// Every case sent from the old support form, by any company in any environment, is also answered by the
// agent in the background, to see how it does on real questions before anyone sees it. Nothing is shown,
// awaited or stored: an error stays silent (the API client shows none unless asked), and the run goes on
// if the form closes. Sentry gets codes, ids and counts only, as for the chat.
const useAgentShadow = () => {
  const agentService = useAgentService();
  const companyId = useSelector((state: AppState) => state.auth.selectedCompany.id);

  const run = async (caseId: number, text: string, hasAttachments: boolean) => {
    // The tracking id of the run, in place of a conversation's.
    const context: AgentTrackingContext = { conversationId: window.crypto.randomUUID(), sessionId: null, companyId };
    const started = performance.now();
    const elapsedMs = () => Math.round(performance.now() - started);

    try {
      const { sessionId } = await agentService.createSession();
      context.sessionId = sessionId;
      const reply = await agentService.sendMessage(sessionId, text.slice(0, MaxQuestionChars), hasAttachments);
      trackAgent(context, "shadow.answered", {
        caseId,
        kind: reply.kind,
        outcome: reply.kind === "answer" ? reply.outcome : undefined,
        sessionEnded: reply.kind === "answer" ? reply.sessionEnded : undefined,
        faqCited: reply.kind === "answer" ? citedFaqArticles(reply.text).join(",") : undefined,
        ms: elapsedMs(),
      });
    } catch (error) {
      trackAgent(context, "shadow.failed", {
        caseId,
        status: error instanceof AgentServiceError ? error.status : 0,
        ms: elapsedMs(),
      });
    }
  };

  /** A case sent from the old support form: its subject and message go to the agent as one question. */
  const onCaseSent = (caseId: number, subject: string, message: string, hasAttachments: boolean) =>
    quietly(() => run(caseId, `${subject}\n\n${message}`, hasAttachments));

  return { onCaseSent };
};

export default useAgentShadow;
